import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Car,
  Search,
  TrendingUp,
  TrendingDown,
  BarChart3,
  AlertTriangle,
  ExternalLink,
  Loader2,
  DollarSign,
  Gauge,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  Info,
  List,
  Settings2,
  RotateCcw,
  Sparkles,
  Database,
  Wifi,
  Server,
  CheckCircle2,
  Activity,
  LogOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "")}/api`
  : "/api";

const formatPrice = (price) => {
  if (!price) return "-";
  return new Intl.NumberFormat("es-UY", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

const formatKm = (km) => {
  if (!km) return "-";
  return `${new Intl.NumberFormat("es-UY").format(km)} km`;
};

// Componente de carga tecnológico
const TechLoader = ({ isVisible, vehicleInfo, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const steps = [
    { icon: Wifi, label: "Configurando conexión segura", sublabel: "Estableciendo proxies..." },
    { icon: Server, label: "Conectando con MercadoLibre", sublabel: "Accediendo al marketplace..." },
    { icon: Search, label: "Buscando publicaciones", sublabel: `${vehicleInfo || "Vehículo"}...` },
    { icon: Database, label: "Extrayendo datos", sublabel: "Procesando listados encontrados..." },
    { icon: Activity, label: "Calculando estadísticas", sublabel: "Analizando precios del mercado..." },
    { icon: CheckCircle2, label: "¡Listo!", sublabel: "Preparando resultados..." },
  ];

  // Controlar cuándo mostrar el loader
  useEffect(() => {
    if (isVisible && !shouldRender) {
      // Iniciar loader
      setShouldRender(true);
      setIsExiting(false);
      setProgress(0);
      setCurrentStep(0);
    } else if (!isVisible && shouldRender && !isExiting) {
      // Comenzar secuencia de salida
      setIsExiting(true);
    }
  }, [isVisible, shouldRender, isExiting]);

  // Manejar la animación de salida
  useEffect(() => {
    if (isExiting) {
      // Completar la barra rápidamente
      setProgress(100);
      setCurrentStep(steps.length - 1);

      // Esperar y luego ocultar
      const exitTimer = setTimeout(() => {
        setShouldRender(false);
        setIsExiting(false);
        setProgress(0);
        setCurrentStep(0);
        if (onComplete) onComplete();
      }, 1200);

      return () => clearTimeout(exitTimer);
    }
  }, [isExiting, steps.length, onComplete]);

  // Animación de progreso
  useEffect(() => {
    if (!shouldRender || isExiting) return;

    // Progreso continuo asintótico hasta 99%
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 99) return 99;
        // Se hace más lento proporcionalmente para que un request de 60-90s se sienta natural
        const remaining = 99 - prev;
        const increment = Math.max(0.015, remaining * 0.006);
        return prev + increment;
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [shouldRender, isExiting]);

  // Animación de pasos
  useEffect(() => {
    if (!shouldRender || isExiting) return;

    const stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        // El último step (5) es "¡Listo!", solo se setea al final en el hook de salida.
        // El step 4 es "Calculando". Si llegamos ahí y sigue cargando, volvemos a "Buscando publicaciones" (2)
        // para dar el efecto de que sigue paginando y buscando en ML.
        if (prev >= steps.length - 2) {
          return 2;
        }
        return prev + 1;
      });
    }, 2500); // 2.5 segundos por texto da tiempo de sobra para leer

    return () => clearInterval(stepInterval);
  }, [shouldRender, isExiting, steps.length]);

  if (!shouldRender) return null;

  const CurrentIcon = steps[currentStep].icon;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-slate-950/98 backdrop-blur-sm transition-opacity duration-700 ${isExiting ? 'opacity-0' : 'opacity-100'
        }`}
      data-testid="tech-loader"
    >
      <div className={`w-full max-w-md mx-4 transition-all duration-700 ${isExiting ? 'scale-98 opacity-0 translate-y-2' : 'scale-100 opacity-100 translate-y-0'
        }`}>
        {/* Card principal - diseño minimalista */}
        <div className="bg-white rounded-xl p-8 shadow-2xl">

          {/* Header simple */}
          <div className="flex items-center gap-3 mb-8 pb-6 border-b border-slate-100">
            <div className="h-10 w-10 rounded-lg bg-slate-900 flex items-center justify-center">
              <Car className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">AutoValúa</h2>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Procesando</p>
            </div>
          </div>

          {/* Icono del paso actual */}
          <div className="flex justify-center mb-6">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-300 ${isExiting ? 'bg-slate-900' : 'bg-slate-100'
              }`}>
              <CurrentIcon className={`h-7 w-7 transition-colors duration-300 ${isExiting ? 'text-white' : 'text-slate-600'
                }`} />
            </div>
          </div>

          {/* Texto del paso actual */}
          <div className="text-center mb-8">
            <p className="text-base font-medium text-slate-900 mb-1">
              {steps[currentStep].label}
            </p>
            <p className="text-sm text-slate-400">
              {steps[currentStep].sublabel}
            </p>
          </div>

          {/* Barra de progreso minimalista */}
          <div className="mb-4">
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${isExiting ? 'duration-1000 ease-in-out bg-slate-900' : 'duration-200 ease-out bg-slate-400'
                  }`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Porcentaje */}
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-300">Progreso</span>
            <span className="text-sm font-medium text-slate-600 tabular-nums">
              {Math.round(progress)}%
            </span>
          </div>
        </div>

        {/* Texto inferior */}
        <p className={`text-center text-xs mt-6 transition-colors duration-300 ${isExiting ? 'text-slate-300' : 'text-slate-500'
          }`}>
          {isExiting ? 'Completado' : 'Analizando datos del mercado'}
        </p>
      </div>
    </div>
  );
};

const ListingsTable = ({ listings }) => {
  if (!listings || listings.length === 0) return null;

  // Filtrar solo USD
  const usdListings = listings.filter(l => l.currency === "USD");
  if (usdListings.length === 0) return null;

  return (
    <Card className="animate-fade-in">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <List className="h-5 w-5" />
          Detalle de Publicaciones
        </CardTitle>
        <CardDescription>{usdListings.length} publicaciones en USD ordenadas por precio</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[400px]">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-50 z-10">
              <TableRow>
                <TableHead className="w-[45%]">Título</TableHead>
                <TableHead className="text-right">Precio</TableHead>
                <TableHead className="text-center">Año</TableHead>
                <TableHead className="text-right">Kilómetros</TableHead>
                <TableHead className="text-center w-[60px]">Ver</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usdListings.map((listing, index) => (
                <TableRow key={listing.item_id} className="hover:bg-slate-50">
                  <TableCell className="font-medium">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="line-clamp-2 cursor-help">{listing.title}</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p>{listing.title}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    US$ {formatPrice(listing.price)}
                  </TableCell>
                  <TableCell className="text-center">{listing.listing_year || "-"}</TableCell>
                  <TableCell className="text-right">{formatKm(listing.listing_km)}</TableCell>
                  <TableCell className="text-center">
                    <a
                      href={listing.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-slate-100 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4 text-primary" />
                    </a>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

const ValuacionDashboard = () => {
  const [formData, setFormData] = useState({
    marca: "",
    modelo: "",
    version: "",
    kilometros: "",
  });

  const [anios, setAnios] = useState([""]);
  const [usarMargenAnio, setUsarMargenAnio] = useState(false);
  const [margenAnio, setMargenAnio] = useState("0");

  const [usarMargenKm, setUsarMargenKm] = useState(false);
  const [margenKm, setMargenKm] = useState("0");

  const [loading, setLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [pendingResult, setPendingResult] = useState(null);
  const [result, setResult] = useState(null);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [showListings, setShowListings] = useState(false);
  const [showRefineSearch, setShowRefineSearch] = useState(false);

  // Estados para refinamiento rápido
  const [refineVersion, setRefineVersion] = useState("");
  const [refineExtraKm, setRefineExtraKm] = useState(0);
  const [refineExtraAnio, setRefineExtraAnio] = useState(0);

  // Tiempo mínimo de la animación (en ms)
  const MIN_LOADER_TIME = 6000;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAnioChange = (index, value) => {
    const newAnios = [...anios];
    newAnios[index] = value;
    setAnios(newAnios);
  };

  const addAnio = () => {
    if (anios.length < 5) {
      setAnios([...anios, ""]);
      setUsarMargenAnio(false);
    }
  };

  const removeAnio = (index) => {
    if (anios.length > 1) {
      setAnios(anios.filter((_, i) => i !== index));
    }
  };

  const getAniosFinales = () => {
    const aniosValidos = anios.filter(a => a && !isNaN(parseInt(a))).map(a => parseInt(a));
    if (aniosValidos.length === 0) return [];

    if (usarMargenAnio && aniosValidos.length === 1) {
      const anioBase = aniosValidos[0];
      const margen = parseInt(margenAnio) || 0;
      const resultado = [];
      for (let i = anioBase - margen; i <= anioBase + margen; i++) {
        resultado.push(i);
      }
      return resultado;
    }
    return aniosValidos;
  };

  const getKmRange = () => {
    const km = parseInt(formData.kilometros);
    if (!km) return null;
    const margen = parseInt(margenKm) || 0;
    return {
      min: Math.round(km * (1 - margen / 100)),
      max: Math.round(km * (1 + margen / 100)),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const aniosFinales = getAniosFinales();

    if (!formData.marca || !formData.modelo || aniosFinales.length === 0 || !formData.kilometros) {
      toast.error("Por favor complete marca, modelo, al menos un año y kilómetros");
      return;
    }

    // Iniciar animación y limpiar estados
    setLoading(true);
    setShowLoader(true);
    setResult(null);
    setPendingResult(null);
    setShowMoreInfo(false);
    setShowListings(false);
    setShowRefineSearch(false);
    setRefineVersion("");
    setRefineExtraKm(0);
    setRefineExtraAnio(0);

    const startTime = Date.now();

    try {
      const payload = {
        marca: formData.marca.trim(),
        modelo: formData.modelo.trim(),
        anios: aniosFinales,
        kilometros: parseInt(formData.kilometros),
        version: formData.version.trim() || null,
        margen_km_porcentaje: usarMargenKm ? parseInt(margenKm) : null,
      };

      const response = await axios.post(`${API}/valuacion`, payload, { timeout: 90000 });

      // Guardar resultado pendiente
      setPendingResult({
        data: response.data,
        success: true,
        usdCount: response.data.stats_usd?.count || 0
      });

    } catch (error) {
      console.error("Error:", error);
      setPendingResult({
        data: null,
        success: false,
        error: error.response?.data?.detail || "Error al calcular valuación",
        status: error.response?.status
      });
    }

    // Calcular tiempo restante para completar la animación mínima
    const elapsed = Date.now() - startTime;
    const remainingTime = Math.max(0, MIN_LOADER_TIME - elapsed);

    // Esperar el tiempo restante antes de cerrar el loader
    setTimeout(() => {
      setLoading(false);
    }, remainingTime);
  };

  // Efecto para mostrar resultados después de que el loader termine
  useEffect(() => {
    if (!loading && !showLoader && pendingResult) {
      if (pendingResult.success) {
        setResult(pendingResult.data);
        setShowMoreInfo(false);
        setShowListings(false);

        const meta = pendingResult.data.metadata || {};
        const totalScraped = meta.total_scraped || 0;
        const totalDiscarded = meta.total_discarded_by_filters || 0;

        if (pendingResult.usdCount > 0) {
          const msg = pendingResult.isRefine
            ? `Búsqueda ampliada: ${pendingResult.usdCount} publicaciones en USD`
            : `Se encontraron ${pendingResult.usdCount} publicaciones en USD`;
          toast.success(msg);
        } else if (totalScraped > 0 && totalDiscarded === totalScraped) {
          toast.warning(`Búsqueda finalizada sin coincidencias exactas. Se evaluaron ${totalScraped} anuncios similares, pero ${totalDiscarded} fueron descartados por no ajustarse al Kilometraje o Año solicitado.`, { duration: 6000 });
        } else {
          toast.warning("No se encontraron publicaciones en USD con esos criterios en MercadoLibre.");
        }
      } else {
        // Manejo ultra granular de Errores
        if (pendingResult.error === "PROXY_BLOCKED" || pendingResult.status === 429) {
          toast.error("No es posible realizar solicitudes en este momento debido a extrema saturación de seguridad en la red (Posible Bloqueo). Por favor, reintente más tarde.", { duration: 6000 });
        } else if (pendingResult.error === "HTML_CHANGED" || pendingResult.status === 502) {
          toast.error("Hubo un error al procesar el catálogo. La estructura de la web de destino pudo haber cambiado. Por favor, contactar al departamento de Sistemas o Soporte.", { duration: 7000 });
        } else {
          toast.error(pendingResult.error || "Error de red o Servidor. Revise su conexión e intente nuevamente.");
        }
      }
      setPendingResult(null);
    }
  }, [loading, showLoader, pendingResult]);

  // Callback cuando el loader termina su animación de salida
  const handleLoaderComplete = () => {
    setShowLoader(false);
  };

  // Función para aplicar refinamientos desde la tarjeta de resultados
  const handleRefineSearch = async () => {
    if (!result) return;

    // Calcular nuevos años si se amplió el rango
    let newAnios = [...(result.anios || [])];
    if (refineExtraAnio > 0) {
      const minAnio = Math.min(...newAnios);
      const maxAnio = Math.max(...newAnios);
      for (let i = 1; i <= refineExtraAnio; i++) {
        if (!newAnios.includes(minAnio - i)) newAnios.push(minAnio - i);
        if (!newAnios.includes(maxAnio + i)) newAnios.push(maxAnio + i);
      }
      newAnios.sort((a, b) => a - b);
    }

    // Calcular nuevo margen de KM
    const currentKmMargin = result.margen_km_porcentaje || (usarMargenKm ? parseInt(margenKm) : 0);
    const newKmMargin = currentKmMargin + refineExtraKm;

    // Nueva versión
    const newVersion = refineVersion.trim() || result.version || null;

    // Iniciar animación
    setLoading(true);
    setShowLoader(true);
    setShowRefineSearch(false);
    setResult(null);
    setPendingResult(null);

    const startTime = Date.now();

    try {
      const payload = {
        marca: result.marca,
        modelo: result.modelo,
        anios: newAnios,
        kilometros: result.kilometros,
        version: newVersion,
        margen_km_porcentaje: newKmMargin > 0 ? newKmMargin : null,
      };

      const response = await axios.post(`${API}/valuacion`, payload, { timeout: 90000 });

      // Reset refinement states
      setRefineVersion("");
      setRefineExtraKm(0);
      setRefineExtraAnio(0);

      setPendingResult({
        data: response.data,
        success: true,
        usdCount: response.data.stats_usd?.count || 0,
        isRefine: true
      });

    } catch (error) {
      console.error("Error:", error);
      setPendingResult({
        data: null,
        success: false,
        error: error.response?.data?.detail || "Error al refinar búsqueda"
      });
    }

    // Calcular tiempo restante
    const elapsed = Date.now() - startTime;
    const remainingTime = Math.max(0, MIN_LOADER_TIME - elapsed);

    setTimeout(() => {
      setLoading(false);
    }, remainingTime);
  };

  // Función para nueva búsqueda (limpia todo)
  const handleNewSearch = () => {
    setResult(null);
    setShowMoreInfo(false);
    setShowListings(false);
    setShowRefineSearch(false);
    setRefineVersion("");
    setRefineExtraKm(0);
    setRefineExtraAnio(0);
    // Scroll al formulario
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const aniosFinales = getAniosFinales();
  const kmRange = getKmRange();
  const stats = result?.stats_usd;
  const hasResults = stats && stats.count > 0;

  // Info del vehículo para el loader
  const vehicleInfo = formData.marca && formData.modelo
    ? `${formData.marca} ${formData.modelo} ${aniosFinales[0] || ''}`
    : null;

  return (
    <div className="min-h-screen bg-background">
      {/* Tech Loader Animation */}
      <TechLoader
        isVisible={loading}
        vehicleInfo={vehicleInfo}
        onComplete={handleLoaderComplete}
      />

      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900 sticky top-0 z-50">
        <div className="container-app py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img 
              src={`${process.env.PUBLIC_URL}/logo-valuauto-blanco.svg`} 
              alt="Valuauto Logo" 
              className="h-7 md:h-8" 
            />
            <div className="hidden sm:block pl-4 border-l border-slate-700">
              <h1 className="text-base md:text-lg font-medium text-slate-200 tracking-wide">
                Sistema de Valuación
              </h1>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={() => {
              localStorage.removeItem("isAuthenticated");
              window.location.href = "/login";
            }}
          >
            <LogOut className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Cerrar Sesión</span>
          </Button>
        </div>
      </header>

      <main className="container-app">
        <Card className="search-card" data-testid="search-form-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Buscar Valuación
            </CardTitle>
            <CardDescription>
              Ingresá los datos del vehículo para obtener estadísticas de mercado
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Marca, Modelo, Versión */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="marca">Marca *</Label>
                  <Input
                    id="marca"
                    name="marca"
                    placeholder="Ej: Toyota"
                    value={formData.marca}
                    onChange={handleInputChange}
                    className="h-11"
                    data-testid="input-marca"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modelo">Modelo *</Label>
                  <Input
                    id="modelo"
                    name="modelo"
                    placeholder="Ej: Corolla"
                    value={formData.modelo}
                    onChange={handleInputChange}
                    className="h-11"
                    data-testid="input-modelo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="version">Versión (opcional)</Label>
                  <Input
                    id="version"
                    name="version"
                    placeholder="Ej: XEI, SE-G"
                    value={formData.version}
                    onChange={handleInputChange}
                    className="h-11"
                    data-testid="input-version"
                  />
                </div>
              </div>

              {/* Año(s) */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Año(s) del vehículo *</Label>
                  {anios.length === 1 && (
                    <div className="flex items-center gap-2">
                      <Switch
                        id="margen-anio"
                        checked={usarMargenAnio}
                        onCheckedChange={setUsarMargenAnio}
                        data-testid="switch-margen-anio"
                      />
                      <Label htmlFor="margen-anio" className="text-sm font-normal text-slate-600 cursor-pointer">
                        Ampliar rango
                      </Label>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 items-center">
                  {anios.map((anio, index) => (
                    <div key={index} className="flex items-center gap-1">
                      <Input
                        type="number"
                        placeholder="2020"
                        min="1990"
                        max="2026"
                        value={anio}
                        onChange={(e) => handleAnioChange(index, e.target.value)}
                        className="h-11 w-28 text-center"
                        data-testid={`input-anio-${index}`}
                      />
                      {anios.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeAnio(index)}
                          className="h-8 w-8 text-slate-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}

                  {!usarMargenAnio && anios.length < 5 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addAnio}
                      className="h-11"
                      data-testid="btn-add-anio"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Agregar año
                    </Button>
                  )}
                </div>

                {usarMargenAnio && anios.length === 1 && (
                  <div className="flex flex-wrap items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-blue-700">Ampliar</span>
                      <span className="text-blue-700 font-bold text-lg">±</span>
                      <Input
                        type="number"
                        min="0"
                        max="10"
                        value={margenAnio}
                        onChange={(e) => setMargenAnio(e.target.value)}
                        className="h-10 w-20 text-center text-lg font-semibold"
                        data-testid="input-margen-anio"
                      />
                      <span className="text-sm font-medium text-blue-700">años</span>
                    </div>
                    {anios[0] && (
                      <div className="text-sm text-blue-600">
                        <span className="font-medium">Buscará:</span>{" "}
                        <span className="font-bold">{aniosFinales.join(", ") || "Ingresá un año"}</span>
                      </div>
                    )}
                  </div>
                )}

                {anios.length > 1 && aniosFinales.length > 0 && (
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Años seleccionados:</span> {aniosFinales.join(", ")}
                  </p>
                )}
              </div>

              {/* Kilómetros */}
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Kilómetros *</Label>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="margen-km"
                      checked={usarMargenKm}
                      onCheckedChange={setUsarMargenKm}
                      data-testid="switch-margen-km"
                    />
                    <Label htmlFor="margen-km" className="text-sm font-normal text-slate-600 cursor-pointer">
                      Ampliar rango
                    </Label>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <Input
                    id="kilometros"
                    name="kilometros"
                    type="number"
                    placeholder="50000"
                    min="0"
                    value={formData.kilometros}
                    onChange={handleInputChange}
                    className="h-11 w-36"
                    data-testid="input-kilometros"
                  />
                </div>

                {usarMargenKm && (
                  <div className="flex flex-wrap items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-blue-700">Ampliar</span>
                      <span className="text-blue-700 font-bold text-lg">±</span>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={margenKm}
                        onChange={(e) => setMargenKm(e.target.value)}
                        className="h-10 w-20 text-center text-lg font-semibold"
                        data-testid="input-margen-km"
                      />
                      <span className="text-sm font-medium text-blue-700">%</span>
                    </div>
                    {kmRange && (
                      <div className="text-sm text-blue-600">
                        <span className="font-medium">Buscará:</span>{" "}
                        <span className="font-bold">{kmRange.min.toLocaleString()} a {kmRange.max.toLocaleString()} km</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Botón Submit */}
              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={loading} className="h-12 px-8 text-base" data-testid="submit-valuacion-btn">
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <DollarSign className="mr-2 h-5 w-5" />
                      Calcular valor de mercado
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <div className="space-y-6 animate-fade-in mt-8" data-testid="results-section">

            {/* Resultado Principal - Precio Promedio Sugerido (USD Only) */}
            {hasResults ? (
              <Card className="border-0 shadow-xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden relative">
                {/* Background decoration */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-primary rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-500 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
                </div>

                <CardContent className="pt-10 pb-10 relative z-10">
                  <div className="text-center">
                    {/* Label superior */}
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full mb-6">
                      <div className="h-2 w-2 bg-emerald-400 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-white/80 uppercase tracking-wider">
                        Valor de mercado estimado
                      </span>
                    </div>

                    {/* Precio principal */}
                    <div className="mb-4">
                      <span className="text-6xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight">
                        US$ {formatPrice(stats.avg_price)}
                      </span>
                    </div>

                    {/* Badge de publicaciones */}
                    <div className="flex items-center justify-center gap-2 mb-6">
                      <Badge variant="secondary" className="bg-white/10 text-white/90 border-white/20 px-4 py-1.5 text-sm font-medium">
                        <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
                        Basado en {stats.count} publicaciones en USD
                      </Badge>
                    </div>

                    {/* Rango típico */}
                    {stats.p25 && stats.p75 && (
                      <div className="inline-flex items-center gap-3 px-5 py-3 bg-emerald-500/20 border border-emerald-400/30 rounded-xl">
                        <Gauge className="h-5 w-5 text-emerald-400" />
                        <div className="text-left">
                          <p className="text-xs text-emerald-300/80 uppercase tracking-wide">Rango típico del mercado</p>
                          <p className="text-lg font-bold text-white">
                            US$ {formatPrice(stats.p25)} – US$ {formatPrice(stats.p75)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Warning muestra pequeña */}
                    {stats.low_sample_warning && (
                      <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-400/30 rounded-lg">
                        <AlertTriangle className="h-4 w-4 text-amber-400" />
                        <span className="text-sm text-amber-200">
                          Muestra pequeña — los resultados pueden variar
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info del vehículo buscado */}
                  <div className="mt-8 pt-6 border-t border-white/10 text-center">
                    <p className="text-sm text-white/60">
                      <span className="font-semibold text-white/80">{result.marca} {result.modelo}</span>
                      <span className="mx-2 text-white/30">•</span>
                      Años: {result.anios?.join(", ")}
                      <span className="mx-2 text-white/30">•</span>
                      {formatKm(result.kilometros)}
                      {result.version && (
                        <>
                          <span className="mx-2 text-white/30">•</span>
                          {result.version}
                        </>
                      )}
                    </p>
                  </div>

                  {/* Botones de acción rápida */}
                  <div className="mt-6 flex flex-wrap gap-3 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRefineSearch(!showRefineSearch)}
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white gap-2"
                      data-testid="btn-refine-search"
                    >
                      <Settings2 className="h-4 w-4" />
                      {showRefineSearch ? "Cerrar opciones" : "Ampliar búsqueda"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNewSearch}
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white gap-2"
                      data-testid="btn-new-search"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Nueva búsqueda
                    </Button>
                  </div>

                  {/* Panel de refinamiento expandible */}
                  {showRefineSearch && (
                    <div className="mt-6 p-5 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 animate-fade-in">
                      <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-amber-400" />
                        Refinar búsqueda
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Agregar versión */}
                        {!result.version && (
                          <div className="space-y-2">
                            <Label className="text-white/70 text-xs uppercase tracking-wide">
                              Agregar versión
                            </Label>
                            <Input
                              placeholder="Ej: XEI, SE-G"
                              value={refineVersion}
                              onChange={(e) => setRefineVersion(e.target.value)}
                              className="bg-white/10 border-white/20 text-white placeholder:text-white/40 h-10"
                              data-testid="input-refine-version"
                            />
                          </div>
                        )}

                        {/* Ampliar años */}
                        <div className="space-y-2">
                          <Label className="text-white/70 text-xs uppercase tracking-wide">
                            Ampliar años ±
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="5"
                              value={refineExtraAnio}
                              onChange={(e) => setRefineExtraAnio(parseInt(e.target.value) || 0)}
                              className="bg-white/10 border-white/20 text-white h-10 w-20 text-center"
                              data-testid="input-refine-anio"
                            />
                            <span className="text-white/60 text-sm">años</span>
                          </div>
                          {refineExtraAnio > 0 && (
                            <p className="text-xs text-emerald-300">
                              Buscará: {Math.min(...(result.anios || [])) - refineExtraAnio} – {Math.max(...(result.anios || [])) + refineExtraAnio}
                            </p>
                          )}
                        </div>

                        {/* Ampliar KM */}
                        <div className="space-y-2">
                          <Label className="text-white/70 text-xs uppercase tracking-wide">
                            Ampliar KM ±
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="50"
                              step="5"
                              value={refineExtraKm}
                              onChange={(e) => setRefineExtraKm(parseInt(e.target.value) || 0)}
                              className="bg-white/10 border-white/20 text-white h-10 w-20 text-center"
                              data-testid="input-refine-km"
                            />
                            <span className="text-white/60 text-sm">%</span>
                          </div>
                          {refineExtraKm > 0 && (
                            <p className="text-xs text-emerald-300">
                              +{refineExtraKm}% de margen adicional
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Botón aplicar */}
                      <div className="mt-5 flex justify-end">
                        <Button
                          onClick={handleRefineSearch}
                          disabled={loading || (refineExtraAnio === 0 && refineExtraKm === 0 && !refineVersion.trim())}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
                          data-testid="btn-apply-refine"
                        >
                          {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Search className="h-4 w-4" />
                          )}
                          Aplicar cambios
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="pt-8 pb-8">
                  <div className="text-center">
                    <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <p className="text-lg font-semibold text-amber-800 mb-2">
                      No se encontraron publicaciones en USD
                    </p>
                    <p className="text-amber-700">
                      {result.message}
                    </p>
                    <p className="text-sm text-amber-600 mt-4">
                      Probá ampliar los rangos de año o kilómetros
                    </p>

                    {/* Botones de acción para caso sin resultados */}
                    <div className="mt-6 flex flex-wrap gap-3 justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowRefineSearch(!showRefineSearch)}
                        className="gap-2"
                        data-testid="btn-refine-search-empty"
                      >
                        <Settings2 className="h-4 w-4" />
                        {showRefineSearch ? "Cerrar opciones" : "Ampliar búsqueda"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNewSearch}
                        className="gap-2"
                        data-testid="btn-new-search-empty"
                      >
                        <RotateCcw className="h-4 w-4" />
                        Nueva búsqueda
                      </Button>
                    </div>

                    {/* Panel de refinamiento para caso sin resultados */}
                    {showRefineSearch && (
                      <div className="mt-6 p-5 bg-amber-100/50 rounded-xl border border-amber-200 text-left animate-fade-in">
                        <h4 className="text-sm font-semibold text-amber-900 mb-4 flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-amber-600" />
                          Refinar búsqueda
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Agregar versión */}
                          {!result.version && (
                            <div className="space-y-2">
                              <Label className="text-amber-800 text-xs uppercase tracking-wide">
                                Agregar versión
                              </Label>
                              <Input
                                placeholder="Ej: XEI, SE-G"
                                value={refineVersion}
                                onChange={(e) => setRefineVersion(e.target.value)}
                                className="h-10"
                                data-testid="input-refine-version-empty"
                              />
                            </div>
                          )}

                          {/* Ampliar años */}
                          <div className="space-y-2">
                            <Label className="text-amber-800 text-xs uppercase tracking-wide">
                              Ampliar años ±
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                max="5"
                                value={refineExtraAnio}
                                onChange={(e) => setRefineExtraAnio(parseInt(e.target.value) || 0)}
                                className="h-10 w-20 text-center"
                                data-testid="input-refine-anio-empty"
                              />
                              <span className="text-amber-700 text-sm">años</span>
                            </div>
                            {refineExtraAnio > 0 && (
                              <p className="text-xs text-emerald-700">
                                Buscará: {Math.min(...(result.anios || [])) - refineExtraAnio} – {Math.max(...(result.anios || [])) + refineExtraAnio}
                              </p>
                            )}
                          </div>

                          {/* Ampliar KM */}
                          <div className="space-y-2">
                            <Label className="text-amber-800 text-xs uppercase tracking-wide">
                              Ampliar KM ±
                            </Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                max="50"
                                step="5"
                                value={refineExtraKm}
                                onChange={(e) => setRefineExtraKm(parseInt(e.target.value) || 0)}
                                className="h-10 w-20 text-center"
                                data-testid="input-refine-km-empty"
                              />
                              <span className="text-amber-700 text-sm">%</span>
                            </div>
                            {refineExtraKm > 0 && (
                              <p className="text-xs text-emerald-700">
                                +{refineExtraKm}% de margen adicional
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Botón aplicar */}
                        <div className="mt-5 flex justify-end">
                          <Button
                            onClick={handleRefineSearch}
                            disabled={loading || (refineExtraAnio === 0 && refineExtraKm === 0 && !refineVersion.trim())}
                            className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
                            data-testid="btn-apply-refine-empty"
                          >
                            {loading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Search className="h-4 w-4" />
                            )}
                            Aplicar cambios
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Botones de acciones */}
            {hasResults && (
              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  variant={showMoreInfo ? "default" : "outline"}
                  onClick={() => setShowMoreInfo(!showMoreInfo)}
                  className="gap-2 h-11 px-6"
                  data-testid="btn-more-info"
                >
                  <Info className="h-4 w-4" />
                  {showMoreInfo ? "Ocultar estadísticas" : "Más información"}
                  {showMoreInfo ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                </Button>

                <Button
                  variant={showListings ? "default" : "outline"}
                  onClick={() => setShowListings(!showListings)}
                  className="gap-2 h-11 px-6"
                  data-testid="btn-show-listings"
                >
                  <List className="h-4 w-4" />
                  {showListings ? "Ocultar publicaciones" : `Ver ${stats.count} publicaciones`}
                  {showListings ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                </Button>
              </div>
            )}

            {/* Más Información - Estadísticas Detalladas */}
            {hasResults && showMoreInfo && (
              <Card className="animate-fade-in border-slate-200">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    Estadísticas Detalladas (USD)
                  </CardTitle>
                  <CardDescription>Desglose completo del análisis de precios</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-50 rounded-xl text-center border border-slate-100">
                      <TrendingDown className="h-5 w-5 text-red-400 mx-auto mb-2" />
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Precio Mínimo</p>
                      <p className="text-xl font-bold text-slate-900">US$ {formatPrice(stats.min_price)}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl text-center border border-slate-100">
                      <TrendingUp className="h-5 w-5 text-emerald-400 mx-auto mb-2" />
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Precio Máximo</p>
                      <p className="text-xl font-bold text-slate-900">US$ {formatPrice(stats.max_price)}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl text-center border border-slate-100">
                      <BarChart3 className="h-5 w-5 text-blue-400 mx-auto mb-2" />
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Mediana</p>
                      <p className="text-xl font-bold text-slate-900">US$ {formatPrice(stats.median_price)}</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-xl text-center border border-emerald-200">
                      <Gauge className="h-5 w-5 text-emerald-500 mx-auto mb-2" />
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Percentil 25-75</p>
                      <p className="text-lg font-bold text-emerald-700">
                        {formatPrice(stats.p25)} – {formatPrice(stats.p75)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                    <p className="text-sm text-blue-800 leading-relaxed">
                      <strong className="text-blue-900">¿Cómo interpretar?</strong> La <span className="font-semibold">mediana</span> indica que el 50% de los autos se publican por debajo de ese precio.
                      El <span className="font-semibold">rango P25-P75</span> muestra dónde está el 50% central del mercado, excluyendo extremos.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Listado de Publicaciones */}
            {hasResults && showListings && (
              <ListingsTable listings={result.listings} />
            )}
          </div>
        )}

        {/* Empty State */}
        {!result && !loading && (
          <div className="text-center py-16 animate-fade-in" data-testid="empty-state">
            <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Car className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Comenzá tu búsqueda</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              Ingresá la marca, modelo, año y kilometraje del vehículo para obtener una estimación de su valor de mercado.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-16 py-8 bg-white">
        <div className="container-app">
          <p className="text-center text-sm text-slate-500">
            Datos obtenidos de Mercado Libre Uruguay. Precios en dólares americanos (USD).
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ValuacionDashboard;
